/**
 * Diálogo de personalización del dashboard (por usuario).
 *
 * Lista TODOS los widgets habilitados para el tenant (los que el SysAdmin
 * otorgó vía TenantWidgetAccess) y deja que cada usuario decida cuáles
 * incluir en SU dashboard, con un switch por widget. La preferencia se
 * guarda en UserWidgetConfig (scope por usuario) vía updateWidgetConfig.
 *
 * Default ENCENDIDO: un widget recién habilitado por el SysAdmin aparece
 * prendido para todos; el usuario lo apaga si no lo quiere (enabledWidgets
 * incluye salvo userConfig.enabled === false).
 */

import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
} from '@mui/material';
import { useRootStore } from '../../../store/contexts';
import { AvailableWidget } from '../../store/DashboardStore';

export interface DashboardCustomizeDialogProps {
  open: boolean;
  onClose: () => void;
}

export const DashboardCustomizeDialog: React.FC<DashboardCustomizeDialogProps> =
  observer(({ open, onClose }) => {
    const { dashboardStore, langStore } = useRootStore();
    const widgets = dashboardStore.widgets;

    const isOn = (w: AvailableWidget) => w.userConfig?.enabled !== false;

    const toggle = (w: AvailableWidget) => {
      void dashboardStore.updateWidgetConfig(w.widgetKey, {
        enabled: !isOn(w),
      });
    };

    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{langStore.dashboardCustomizeTitle}</DialogTitle>
        <DialogContent dividers>
          {widgets.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {langStore.dashboardCustomizeEmpty}
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {langStore.dashboardCustomizeHint}
              </Typography>
              <List dense disablePadding>
                {widgets.map((w) => (
                  <ListItem
                    key={w.widgetKey}
                    disableGutters
                    secondaryAction={
                      <Switch
                        edge="end"
                        checked={isOn(w)}
                        onChange={() => toggle(w)}
                      />
                    }
                  >
                    <ListItemText primary={w.title} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{langStore.closeCommand}</Button>
        </DialogActions>
      </Dialog>
    );
  });

export default DashboardCustomizeDialog;
